import { pb } from '../helpers/pocketbase_helper';
import { ExcelExportService } from './ExcelExportService';
import { ProcessDetectionService } from './ProcessDetectionService';
import { ColorDetectionService } from './ColorDetectionService';

export const TareasExportService = {
  /**
   * Obtiene las tareas activas de un centro de costo específico
   * @param {string} centroCostoId - ID del centro de costo
   * @returns {Promise<Array>} Lista de tareas ordenadas por prioridad
   */
  async getTareasByCentroCosto(centroCostoId) {
    try {
      if (!centroCostoId) {
        throw new Error('ID de centro de costo requerido');
      }

      const result = await pb.collection('log_plan_centro_costo').getList(1, 100, {
        filter: `centro_costo = "${centroCostoId}" && fin = null`,
        expand: 'orden,orden.producto,orden.corte_conversion,orden.corte_conversion.parametro,orden.corte_conversion.parametro.impresion,orden.corte_conversion.parametro.impresion.parametros_tinta,orden.corte_conversion.parametro.impresion.parametros_tinta.tinta',
        sort: 'prioridad'
      });

      return result.items;
    } catch (error) {
      console.error('Error fetching tareas:', error);
      throw new Error('Error al cargar tareas del centro de costo');
    }
  },

  /**
   * Verifica si un centro de costo es de proceso de impresión
   * @param {Object} centroCosto - Centro de costo
   * @returns {boolean} True si es proceso de impresión
   */
  isImpresionProcess(centroCosto) {
    return ProcessDetectionService.isImpresionProcess(centroCosto);
  },

  /**
   * Transforma las tareas al formato requerido para Excel
   * @param {Array} tareas - Lista de tareas
   * @param {Object} centroCosto - Centro de costo
   * @returns {Array} Tareas transformadas
   */
  transformTareasForExcel(tareas, centroCosto) {
    const isImpresion = ProcessDetectionService.isImpresionProcess(centroCosto);

    return tareas.map(tarea => {
      const orden = tarea.expand?.orden;
      const producto = orden?.expand?.producto;

      const baseData = {
        numero_orden: orden?.numero || 'N/A',
        producto: producto?.nombre || orden?.producto || 'N/A',
        listo_para_empezar: tarea.ready ? 'Sí' : 'No',
        tiraje_tamanos: this.extractTirajeTamanosFromOrden(orden),
        fecha_entrega: orden?.fecha_entrega ? new Date(orden.fecha_entrega).toLocaleDateString('es-ES') : 'N/A'
      };

      // Agregar columnas de impresión si es necesario
      if (isImpresion) {
        // Extraer datos de colores desde corte_conversion -> parametros -> impresion
        const colorData = this.extractColorDataFromOrden(orden);
        // Extraer observaciones de impresión
        const observaciones = this.extractObservacionesFromOrden(orden);
        return {
          ...baseData,
          c: colorData.c,
          m: colorData.m,
          y: colorData.y,
          k: colorData.k,
          pantone: colorData.pantone,
          observaciones: observaciones
        };
      }

      return baseData;
    });
  },

  /**
   * Extrae datos de colores desde la estructura de orden -> corte_conversion -> parametros -> impresion -> parametros_tinta
   * @param {Object} orden - Orden de producción
   * @returns {Object} Datos de colores extraídos
   */
  extractColorDataFromOrden(orden) {
    
    // Verificar si hay corte_conversion expandido
    if (!orden?.expand?.corte_conversion || !Array.isArray(orden.expand.corte_conversion)) {
      return {
        c: '',
        m: '',
        y: '',
        k: '',
        pantone: ''
      };
    }


    // Buscar en todos los corte_conversion expandidos y sus parametros
    for (const corte of orden.expand.corte_conversion) {
      
      // parametro es un array, necesitamos iterar sobre él
      if (corte.expand?.parametro && Array.isArray(corte.expand.parametro)) {
        
        for (const parametro of corte.expand.parametro) {
          
          if (parametro.expand?.impresion?.expand?.parametros_tinta) {
            const parametrosTinta = parametro.expand.impresion.expand.parametros_tinta;
            const resultado = this.extractColorsFromParametrosTinta(parametrosTinta);
            return resultado;
          }
        }
      } else {
      }
    }

    // Si no se encuentra ningún dato de impresión, retornar valores vacíos
    return {
      c: '',
      m: '',
      y: '',
      k: '',
      pantone: ''
    };
  },

  /**
   * Extrae colores CMYK y Pantone desde parametros_tinta
   * @param {Array} parametrosTinta - Array de parametros_tinta
   * @returns {Object} Datos de colores extraídos
   */
  extractColorsFromParametrosTinta(parametrosTinta) {
    
    if (!Array.isArray(parametrosTinta)) {
      return {
        c: '',
        m: '',
        y: '',
        k: '',
        pantone: ''
      };
    }

    const colors = {
      c: '',
      m: '',
      y: '',
      k: '',
      pantone: ''
    };
    const pantoneList = [];

    // Buscar colores CMYK y Pantone en los parametros_tinta
    for (const parametro of parametrosTinta) {
      
      if (parametro.expand?.tinta && Array.isArray(parametro.expand.tinta)) {
        
        for (const tinta of parametro.expand.tinta) {
          const nombreTinta = tinta.nombre?.toLowerCase().trim();
          
          if (nombreTinta === 'c') {
            colors.c = '■';
          } else if (nombreTinta === 'm') {
            colors.m = '■';
          } else if (nombreTinta === 'y') {
            colors.y = '■';
          } else if (nombreTinta === 'k') {
            colors.k = '■';
          }
        }
      }
      
      // Extraer nombre del parametro solo si tiene pantone
      
      if (parametro.pantone && parametro.pantone.trim() && parametro.nombre && parametro.nombre.trim()) {
        pantoneList.push(parametro.nombre.trim());
      }
    }

    // Unir todos los pantones con comas
    colors.pantone = pantoneList.join(', ');
    
    return colors;
  },

  /**
   * Extrae observaciones de impresión desde la estructura de orden
   * @param {Object} orden - Orden de producción
   * @returns {string} Observaciones de impresión
   */
  extractObservacionesFromOrden(orden) {
    // Por ahora retornar vacío como solicitado
    return '';
  },

  /**
   * Extrae el tiraje-tamaños desde la estructura de orden
   * @param {Object} orden - Orden de producción
   * @returns {string} Tiraje-tamaños calculado
   */
  extractTirajeTamanosFromOrden(orden) {
    // Verificar si hay corte_conversion expandido
    if (!orden?.expand?.corte_conversion || !Array.isArray(orden.expand.corte_conversion)) {
      return 'N/A';
    }

    let totalTiraje = 0;

    // Buscar en todos los corte_conversion expandidos y sus parametros
    for (const corte of orden.expand.corte_conversion) {
      // parametro es un array, necesitamos iterar sobre él
      if (corte.expand?.parametro && Array.isArray(corte.expand.parametro)) {
        for (const parametro of corte.expand.parametro) {
          if (parametro.expand?.impresion) {
            const impresion = parametro.expand.impresion;
            // Sumar el total de impresión desde impresion_tabla
            if (impresion.total && !isNaN(parseInt(impresion.total))) {
              totalTiraje += parseInt(impresion.total);
            }
          }
        }
      }
    }

    // Si no hay datos de impresión, retornar N/A
    if (totalTiraje === 0) {
      return 'N/A';
    }

    // Retornar solo el total sumado
    return totalTiraje.toString();
  },

  /**
   * Genera el archivo Excel con las tareas
   * @param {string} centroCostoId - ID del centro de costo
   * @param {Array} tareas - Lista de tareas
   * @returns {Promise<void>}
   */
  async generateExcel(centroCostoId, tareas) {
    try {
      if (!tareas || tareas.length === 0) {
        throw new Error('No hay tareas para exportar');
      }

      // Obtener información del centro de costo
      const centroCosto = await this.getCentroCostoInfo(centroCostoId);
      
      // Transformar tareas al formato Excel
      const transformedTareas = this.transformTareasForExcel(tareas, centroCosto);

      // Configuración para Excel
      const config = {
        centroCosto: centroCosto,
        isImpresion: this.isImpresionProcess(centroCosto),
        filename: `centro_costo_${centroCosto.nombre}_${new Date().toISOString().split('T')[0]}.xlsx`
      };

      // Generar Excel usando el servicio
      await ExcelExportService.generateExcel(centroCostoId, transformedTareas, config);

    } catch (error) {
      console.error('Error generating Excel:', error);
      throw error;
    }
  },

  /**
   * Obtiene información del centro de costo
   * @param {string} centroCostoId - ID del centro de costo
   * @returns {Promise<Object>} Información del centro de costo
   */
  async getCentroCostoInfo(centroCostoId) {
    try {
      const centro = await pb.collection('centro_costo').getOne(centroCostoId, {
        expand: 'proceso'
      });

      return {
        id: centro.id,
        nombre: centro.nombre,
        proceso: centro.expand?.proceso?.nombre || 'N/A'
      };
    } catch (error) {
      console.error('Error fetching centro de costo info:', error);
      throw new Error('Error al obtener información del centro de costo');
    }
  },

  /**
   * Obtiene el mapeo de procesos de impresión
   * @returns {Promise<Array>} Lista de procesos de impresión
   */
  async getImpresionProcesses() {
    try {
      const result = await pb.collection('mapa_nombre_proceso_tabla').getList(1, 50, {
        filter: 'proceso = "impresión"',
        sort: 'nombre'
      });

      return result.items;
    } catch (error) {
      console.error('Error fetching impresión processes:', error);
      return [];
    }
  }
};
